import { faClock, faPenToSquare } from "@fortawesome/free-regular-svg-icons";
import {
  faCar,
  faClose,
  faWarning,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect } from "react";

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: {
    qualityAlert: boolean;
    supplierNote: boolean;
    dailyNote: boolean;
    proactiveAudit: boolean;
    additionalHours: boolean;
    checkedIn: boolean;
    dailyProductionVolume: boolean;
  };
  onFilterChange: (filters: {
    qualityAlert: boolean;
    supplierNote: boolean;
    dailyNote: boolean;
    proactiveAudit: boolean;
    additionalHours: boolean;
    checkedIn: boolean;
    dailyProductionVolume: boolean;
  }) => void;
}

const colorMap = new Map<string, string>([
  ["qualityAlert", "#ff453a"],
  ["supplierNote", "#0a84ff"],
  ["dailyNote", "#71d9e2"],
  ["proactiveAudit", "#af52de"],
  ["additionalHours", "#ff9f0a"],
  ["dailyProductionVolume", "#8D8D8D"],
  // ["checkedIn", "#00FF00"],
]);

const iconMap = new Map<string, IconDefinition>([
  ["qualityAlert", faWarning],
  ["supplierNote", faPenToSquare],
  ["dailyNote", faPenToSquare],
  ["proactiveAudit", faPenToSquare],
  ["additionalHours", faClock],
  ["dailyProductionVolume", faCar],
  // ["checkedIn", faPenToSquare],
]);

const capitalizeFirstLetter = (data: string) => {
  return data.charAt(0).toUpperCase() + data.slice(1);
};

const getSortedFilterKeys = (filters: FilterModalProps['filters']) => {
  return Object.keys(filters)
    .filter(filterKey => filterKey !== "checkedIn")
    .sort((firstFilter, secondFilter) => {
      const firstDisplayName = capitalizeFirstLetter(firstFilter.replace(/([A-Z])/g, " $1"));
      const secondDisplayName = capitalizeFirstLetter(secondFilter.replace(/([A-Z])/g, " $1"));
      return firstDisplayName.localeCompare(secondDisplayName);
    });
};

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFilterChange,
}) => {
  const [selectedFilters, setSelectedFilters] = useState(filters);

  useEffect(() => {
    setSelectedFilters(filters);
  }, [filters]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setSelectedFilters((prevFilters) => ({
      ...prevFilters,
      [name]: checked,
    }));
  };

  const handleApplyFilters = () => {
    onFilterChange(selectedFilters);
    onClose();
  };

  const resetFilter = () => {
    const filterCopy = { ...filters };
    Object.keys(filterCopy).forEach((key) => {
      filterCopy[key as keyof typeof filterCopy] = true;
    });
    setSelectedFilters(filterCopy);
    onClose();
  };

  const sortedFilterKeys = getSortedFilterKeys(filters);

  return (
    <>
      {isOpen && (
        <div className="absolute right-[5px] top-[-5px] z-[999999999] w-[250px] rounded-md border-[1px] border-[#E5E5E5] bg-[white] p-[12px] shadow-lg">
          <div className="flex w-[100%] flex-col">
            <div className="mb-[8px] flex items-center justify-between border-b-[1px] pb-[4px]">
              <span className="text-[14px] font-semibold text-[#212322]">
                Filter Options
              </span>
              <FontAwesomeIcon
                className="cursor-pointer"
                icon={faClose}
                onClick={onClose}
              />
            </div>
            {sortedFilterKeys.map(
              (filterKey) => (
                <div
                  key={filterKey}
                  onClick={() =>
                    handleCheckboxChange({
                      target: {
                        name: filterKey,
                        checked:
                          !selectedFilters[filterKey as keyof typeof filters],
                      } as any,
                    } as any)
                  }
                  className="flex w-[100%] cursor-pointer flex-row items-center gap-x-[6px] pb-[3px] hover:bg-gray-50"
                >
                  <input
                    onChange={handleCheckboxChange}
                    checked={
                      selectedFilters[filterKey as keyof typeof filters]
                    }
                    type="checkbox"
                    name={filterKey}
                    className="checkbox max-h-[16px] max-w-[16px] rounded-[50%] border-[#212322] [--chkbg:#fedd00] [--chkfg:black]"
                  />
                  <FontAwesomeIcon
                    style={{ color: colorMap.get(filterKey) }}
                    icon={iconMap.get(filterKey) as IconDefinition}
                  />
                  <span className="text-[14px] font-semibold text-[#212322]">
                    {capitalizeFirstLetter(
                      filterKey.replace(/([A-Z])/g, " $1"),
                    )}
                  </span>
                </div>
              ),
            )}

            <div className="flex w-[100%] flex-row justify-end gap-x-[8px] pt-[16px]">
              <button
                onClick={handleApplyFilters}
                className="h-[26px] w-[160px] rounded-[4px] border-0 bg-[#fedd00] text-[14px] font-medium text-[#212322]"
              >
                Apply Filters
              </button>

              <button
                onClick={() => resetFilter()}
                className="h-[26px] w-[100px] rounded-[4px] border-0 bg-[#212322] text-[14px] font-medium text-[#ffffff]"
              >
                Clear Filter
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FilterModal;
